import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Outlet, createRootRoute, useRouterState, useSearch } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { UserbackProvider, useUserback } from '@userback/react';
import axios from 'axios';
import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';

import { useGetBannerMessageQuery } from '@/api/api-hooks';
import { security } from '@/api/security';
import { AppSkeletonLoader } from '@/components/AppSkeletonLoader';
import { Auth0Apollo } from '@/components/Auth0Apollo';
import { MaintenancePage } from '@/components/MaintenancePage';
import { NotFound404 } from '@/components/NotFound404';
import { Racers } from '@/components/Racers/Racers';
import { TelemetryNotifier } from '@/components/Telemetry/TelemetryNotifier';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { apiAudienceUrl } from '@/config';
import { GlobalProvider, useGlobal } from '@/contexts/GlobalContext';
import { LayoutProvider } from '@/contexts/LayoutContext';
import { TelemetryBarProvider } from '@/contexts/TelemetryBarContext';
import { TelemetryProvider } from '@/contexts/TelemetryContext';
import { handledErrors, showErrorToast } from '@/errors/show-error-toast';
import { useErrorLogging, useLogUser } from '@/hooks/useErrorLogging';
import { useUser } from '@/hooks/useUser';
import { GaProvider } from '@/providers/ga-provider';
import { PostHogProvider } from '@/providers/posthog-provider';
import { ThemeProvider } from '@/providers/theme-provider';
import '@/styles/driver-check-tree.css';
import '@/styles/globals.css';
import { getAuth0AppBaseUrl } from '@/utils/getAuth0AppBaseUrl';
import { stringifySearch } from '@/utils/router';

export const Route = createRootRoute({
  component: App,
  notFoundComponent: () => <NotFound404 />,
});

const userbackToken = import.meta.env.VITE_USERBACK_TOKEN as string;
const apiAudience = apiAudienceUrl;
const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const showRouterDevtools = import.meta.env.VITE_SHOW_ROUTER_DEVTOOLS === 'true';

const UserBackIdentifier: FC = () => {
  const { user } = useUser();
  const Userback = useUserback();
  const { organizationName } = useUser();
  if (user && organizationName) {
    Userback.identify(user?.sub ?? 'no-sub', {
      name: user.name,
      email: user.email,
      // @TODO: get the plan from the user
      plan: 'enterprise',
      account_id: organizationName,
    });
  }
  return null;
};

const OrgCheck: FC<{ children: ReactNode }> = ({ children }) => {
  const { is404 } = useUser();
  const { isOrgSwitching } = useGlobal();

  if (isOrgSwitching) {
    return <AppSkeletonLoader />;
  }

  if (is404) {
    return <NotFound404 />;
  }

  return children;
};

// Make the various auth0 functions available via the security singleton where needed
const ApiSecurityProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { error, isLoading, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0();
  const { isAuthenticated, isMeLoading, orgRole, user } = useUser();
  const query = useSearch({
    strict: false,
  });

  // Make Auth0 functions available globally
  security.setAccessTokenSilently(getAccessTokenSilently);
  security.setLoginWithRedirect(loginWithRedirect);
  security.setUser(user);
  security.setLogout(logout);

  const { invitation, organization, organization_name, ...restQuery } = query;

  const missingRole = !isMeLoading && !orgRole;

  const inviteParams: { invitation?: string; organization?: string; organization_name?: string } = useMemo(() => {
    let inviteParams = {};
    if (invitation) {
      inviteParams = {
        invitation,
        organization,
        organization_name,
      };
    }
    return inviteParams;
  }, [invitation, organization, organization_name]);

  const doLoginWithRedirect = useCallback(() => {
    const returnTo = window.location.pathname + stringifySearch(restQuery);
    loginWithRedirect({
      appState: { returnTo },
      authorizationParams: inviteParams,
    });
  }, [inviteParams, loginWithRedirect, restQuery]);

  useEffect(() => {
    if (!isLoading) {
      // If Auth0 returned an error (e.g. MFA not enrolled), don't attempt
      // re-authentication — that creates an infinite redirect loop.
      if (error) {
        return;
      }

      if (isAuthenticated) {
        // If there is an invitation, force re-login.
        if (inviteParams.invitation) {
          logout({
            logoutParams: {
              returnTo: `${window.location.origin}?invitation=${inviteParams.invitation}&organization=${inviteParams.organization}&organization_name=${inviteParams.organization_name}`,
            },
          });
        }

        // If authenticated but missing roles, force re-login.
        if (missingRole) {
          doLoginWithRedirect();
        }
      } else {
        // Try silent authentication on load if not authenticated.
        getAccessTokenSilently().catch((e) => {
          console.debug(e);

          // If silent auth fails, redirect to login or logout.
          if (e.error === 'login_required' || e.error === 'consent_required') {
            doLoginWithRedirect();
          } else {
            logout({ logoutParams: { returnTo: window.location.origin } });
          }
        });
      }
    }
  }, [
    error,
    inviteParams,
    isLoading,
    isAuthenticated,
    doLoginWithRedirect,
    getAccessTokenSilently,
    logout,
    missingRole,
  ]);

  if (error) {
    console.error(error);
    return (
      <main className="bg-background grid min-h-screen place-items-center">
        <div className="text-center">
          <h1 className="text-xl font-bold">Authentication Error</h1>
          <p className="text-muted-foreground mt-2">An error has occurred while authenticating. Please try again.</p>
          <button
            className="text-primary mt-4 underline underline-offset-4 hover:opacity-80"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            Log out and try again
          </button>
        </div>
      </main>
    );
  }

  if (isMeLoading || !isAuthenticated) {
    return <AppSkeletonLoader />;
  }

  return children;
};

const ErrorLoggingUserProvider: FC = () => {
  useLogUser();
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The default is instant and results in a lot more background refreshes,
      // but 1 second seems to get the behavior closer to what RTK Query was doing.
      staleTime: 1000,
      retry: (failureCount, error) => {
        // Don't retry on 403 (Forbidden) errors as they indicate authorization issues
        // that won't be resolved by retrying
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          return false;
        }
        // Default retry behavior (retry up to 3 times for other errors)
        return failureCount < 3;
      },
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Skip if the mutation already has a component-level onError handler
      if (mutation.options.onError) return;
      // Defer to macrotask so component-level catch blocks around mutateAsync
      // run first. MutationCache.onError fires synchronously, then the promise
      // rejection is delivered as a microtask. queueMicrotask doesn't help
      // because our microtask is queued before the rejection. setTimeout(0)
      // pushes to the macrotask queue, which runs after all microtasks — so
      // the component's catch → showErrorToast → handledErrors.add() completes
      // before we check here.
      setTimeout(() => {
        if (error !== null && typeof error === 'object' && handledErrors.has(error)) return;
        showErrorToast(error, 'An unexpected error occurred');
      }, 0);
    },
  }),
});

const useIsPublicPath = () => {
  // 🐉🐉🐉
  // Cursor spit out this isPublicRoute code, and I'm not 100% sure it will hold up. 🤞
  const isPublicRoute = useRouterState({
    select: (s) => s.matches.some((m) => m.id?.startsWith('/_public')),
  });

  return isPublicRoute;
};

function AppWithHealthCheck() {
  const { data: banner } = useGetBannerMessageQuery();
  const isPublic = useIsPublicPath();

  if (banner?.message) {
    return <MaintenancePage />;
  }

  return (
    <ThemeProvider attribute="class" defaultTheme="light">
      <LayoutProvider>
        <div className="bg-muted h-screen">
          <Racers />
          <div className="font-sans">
            <Auth0Provider
              domain={auth0Domain}
              clientId={clientId}
              cacheLocation="localstorage"
              useRefreshTokens={true}
              useRefreshTokensFallback={true}
              authorizationParams={{
                audience: apiAudience,
                scope: 'openid profile email offline_access read:current_user',
                redirect_uri: getAuth0AppBaseUrl(),
              }}
            >
              {isPublic ? (
                <Outlet />
              ) : (
                <ApiSecurityProvider>
                  <UserbackProvider token={userbackToken}>
                    <OrgCheck>
                      <PostHogProvider>
                        <ErrorLoggingUserProvider />
                        <UserBackIdentifier />
                        <Auth0Apollo>
                          <TelemetryProvider>
                            {/* TelemetryProvider only provides context; toast
                                side effects live in TelemetryNotifier so the
                                provider stays a pure value supplier. */}
                            <TelemetryNotifier />
                            <TelemetryBarProvider>
                              <Outlet />
                            </TelemetryBarProvider>
                          </TelemetryProvider>
                        </Auth0Apollo>
                      </PostHogProvider>
                    </OrgCheck>
                  </UserbackProvider>
                </ApiSecurityProvider>
              )}
            </Auth0Provider>
          </div>
          {showRouterDevtools && <TanStackRouterDevtools position="bottom-right" />}
          <GaProvider />
        </div>
      </LayoutProvider>
    </ThemeProvider>
  );
}

function App() {
  useErrorLogging();

  return (
    <TooltipProvider delayDuration={300}>
      <GlobalProvider>
        <Toaster />
        <QueryClientProvider client={queryClient}>
          <AppWithHealthCheck />
        </QueryClientProvider>
      </GlobalProvider>
    </TooltipProvider>
  );
}
